import { apiRequest } from "../services/apiRequest";

// Helper function to get current language
const getCurrentLanguage = () => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem("language") || "ar";
  }
  return "ar";
};

// Helper function to parse error details and return appropriate message based on language
const parseErrorDetails = (details, defaultMessage = "Error") => {
  if (!details) return defaultMessage;
  
  try {
    let detailsStr = typeof details === 'string' ? details : JSON.stringify(details);
    
    // Handle Django REST Framework ErrorDetail format
    // Example: "{'name': [ErrorDetail(string='This field is required.', code='required')], 'email': [ErrorDetail(string='This field is required.', code='required')]}"
    
    // Extract field errors from ErrorDetail format
    const fieldErrors = [];
    const fieldErrorRegex = /'(\w+)':\s*\[ErrorDetail\(string='([^']+)',\s*code='([^']+)'\)\]/g;
    let match;
    
    while ((match = fieldErrorRegex.exec(detailsStr)) !== null) {
      const fieldName = match[1];
      const errorMessage = match[2];
      fieldErrors.push(`${fieldName}: ${errorMessage}`);
    }
    
    // If we found field errors, format them nicely
    if (fieldErrors.length > 0) {
      const lang = getCurrentLanguage();
      if (lang === "ar") {
        // Arabic error messages
        const arabicFieldNames = {
          'name': 'الاسم',
          'email': 'البريد الإلكتروني',
          'phone': 'الهاتف',
          'contact': 'قائمة الاتصال'
        };
        
        const formattedErrors = fieldErrors.map(err => {
          const [field, message] = err.split(': ');
          const arabicField = arabicFieldNames[field] || field;
          const arabicMessage = message === 'This field is required.' 
            ? 'هذا الحقل مطلوب' 
            : message;
          return `${arabicField}: ${arabicMessage}`;
        });
        
        return formattedErrors.join('، ');
      } else {
        // English error messages
        const formattedErrors = fieldErrors.map(err => {
          const [field, message] = err.split(': ');
          return `${field}: ${message}`;
        });
        
        return formattedErrors.join(', ');
      }
    }
    
    // Try to parse as JSON if it's a valid JSON string
    const detailsStrCleaned = detailsStr.replace(/'/g, '"');
    const detailsObj = JSON.parse(detailsStrCleaned);
    
    const lang = getCurrentLanguage();
    // Use Arabic if language is Arabic, otherwise use English
    return lang === "ar" 
      ? (detailsObj.errorArabic || detailsObj.error || defaultMessage)
      : (detailsObj.error || detailsObj.errorArabic || defaultMessage);
  } catch (parseError) {
    // If parsing fails, try to extract any readable error message
    if (typeof details === 'string') {
      // Try to extract simple error messages
      const simpleErrorMatch = details.match(/string='([^']+)'/);
      if (simpleErrorMatch) {
        const lang = getCurrentLanguage();
        const errorMsg = simpleErrorMatch[1];
        if (lang === "ar" && errorMsg === "This field is required.") {
          return "هذا الحقل مطلوب";
        }
        return errorMsg;
      }
      return details;
    }
    return defaultMessage;
  }
};

export const Contacts = {
  getContactLists: async ({ search = "", page = 1, pageSize = 10 } = {}) => {
    const queryParams = new URLSearchParams();

    if (search) {
      // When searching, only add search parameter, no pagination
      queryParams.set("name", search);
    } else {
      // When not searching, add pagination
      queryParams.set("page", page);
      if (pageSize) queryParams.set("page_size", pageSize);
    }

    const queryString = queryParams.toString();
    const url = `/account/contacts/${queryString ? `?${queryString}` : ""}`;

    return await apiRequest("get", url);
  },

  createContactList: async (payload) => {
    try {
      const response = await apiRequest("post", `/account/contacts/`, payload);
      
      // Check if response indicates failure
      if (response?.data?.success === false) {
        const lang = getCurrentLanguage();
        const defaultMessage = lang === "ar" ? "خطأ في إنشاء قائمة الاتصال" : "Error creating contact list";
        const errorMessage = parseErrorDetails(response?.data?.details, defaultMessage);
        
        const error = new Error(errorMessage);
        error.response = {
          data: {
            error: errorMessage,
            ...response.data
          }
        };
        throw error;
      }
      
      return response;
    } catch (error) {
      // Pass through field-specific errors (like {"name": ["error"]}) without modification
      // Only add generic error message if no specific field errors exist
      if (error.response?.data) {
        const errorData = error.response.data;
        const hasFieldErrors = Object.keys(errorData).some(key => 
          Array.isArray(errorData[key]) && key !== 'details'
        );
        
        // Only add generic error if there are no field-specific errors
        if (!hasFieldErrors && errorData.details && !errorData.error) {
          const lang = getCurrentLanguage();
          const defaultMessage = lang === "ar" ? "خطأ في إنشاء قائمة الاتصال" : "Error creating contact list";
          error.response.data.error = parseErrorDetails(errorData.details, defaultMessage);
        }
      }
      throw error;
    }
  },

  updateContactList: async (contactListId, payload) => {
    try {
      const response = await apiRequest("patch", `/account/contacts/${contactListId}/`, payload);
      
      // Check if response indicates failure
      if (response?.data?.success === false) {
        const lang = getCurrentLanguage();
        const defaultMessage = lang === "ar" ? "خطأ في تحديث قائمة الاتصال" : "Error updating contact list";
        const errorMessage = parseErrorDetails(response?.data?.details, defaultMessage);
        
        const error = new Error(errorMessage);
        error.response = {
          data: {
            error: errorMessage,
            ...response.data
          }
        };
        throw error;
      }
      
      return response;
    } catch (error) {
      // Pass through field-specific errors (like {"name": ["error"]}) without modification
      // Only add generic error message if no specific field errors exist
      if (error.response?.data) {
        const errorData = error.response.data;
        const hasFieldErrors = Object.keys(errorData).some(key => 
          Array.isArray(errorData[key]) && key !== 'details'
        );
        
        // Only add generic error if there are no field-specific errors
        if (!hasFieldErrors && errorData.details && !errorData.error) {
          const lang = getCurrentLanguage();
          const defaultMessage = lang === "ar" ? "خطأ في تحديث قائمة الاتصال" : "Error updating contact list";
          error.response.data.error = parseErrorDetails(errorData.details, defaultMessage);
        }
      }
      throw error;
    }
  },

  deleteContactList: async (contactListId) => {
    return await apiRequest("delete", `/account/contacts/${contactListId}/`);
  },

  getContactListById: async (contactListId) => {
    return await apiRequest("get", `/account/contact_users/?contact=${contactListId}`);
  },

  getContactsByContactId: async (contactId, { search = "", page = 1, pageSize = 10 } = {}) => {
    const queryParams = new URLSearchParams();
    queryParams.set("contact", contactId);
    
    if (search) {
      // When searching, only add search parameter, no pagination
      queryParams.set("name", search);
    } else {
      // When not searching, add pagination
      queryParams.set("page", page);
      if (pageSize) queryParams.set("page_size", pageSize);
    }

    const queryString = queryParams.toString();
    const url = `/account/contact_users/?${queryString}`;

    return await apiRequest("get", url);
  },

  getContactsByListId: async (contactListId, { page = 1, pageSize = 10 } = {}) => {
    const queryParams = new URLSearchParams();
    queryParams.set("page", page);
    if (pageSize) queryParams.set("page_size", pageSize);

    const queryString = queryParams.toString();
    const url = `/account/contacts/lists/${contactListId}/contacts/${queryString ? `?${queryString}` : ""}`;

    return await apiRequest("get", url);
  },

  addContactToList: async (contactListId, payload) => {
    try {
      const response = await apiRequest("post", `/account/contacts/lists/${contactListId}/contacts/`, payload);
      
      // Check if response indicates failure
      if (response?.data?.success === false) {
        const lang = getCurrentLanguage();
        const defaultMessage = lang === "ar" ? "خطأ في إضافة جهة اتصال إلى القائمة" : "Error adding contact to list";
        const errorMessage = parseErrorDetails(response?.data?.details, defaultMessage);
        
        const error = new Error(errorMessage);
        error.response = {
          data: {
            error: errorMessage,
            ...response.data
          }
        };
        throw error;
      }
      
      return response;
    } catch (error) {
      if (error.response?.data?.details && !error.response?.data?.error) {
        const lang = getCurrentLanguage();
        const defaultMessage = lang === "ar" ? "خطأ في إضافة جهة اتصال إلى القائمة" : "Error adding contact to list";
        error.response.data.error = parseErrorDetails(error.response.data.details, defaultMessage);
      }
      throw error;
    }
  },

  updateContact: async (contactId, payload) => {
    try {
      const response = await apiRequest("patch", `/account/contact_users/${contactId}/`, payload);
      
      // Check if response indicates failure
      if (response?.data?.success === false) {
        const lang = getCurrentLanguage();
        const defaultMessage = lang === "ar" ? "خطأ في تحديث جهة الاتصال" : "Error updating contact";
        const errorMessage = parseErrorDetails(response?.data?.details, defaultMessage);
        
        const error = new Error(errorMessage);
        error.response = {
          data: {
            error: errorMessage,
            ...response.data
          }
        };
        throw error;
      }
      
      return response;
    } catch (error) {
      if (error.response?.data?.details && !error.response?.data?.error) {
        const lang = getCurrentLanguage();
        const defaultMessage = lang === "ar" ? "خطأ في تحديث جهة الاتصال" : "Error updating contact";
        error.response.data.error = parseErrorDetails(error.response.data.details, defaultMessage);
      }
      throw error;
    }
  },

  deleteContact: async (contactId) => {
    return await apiRequest("delete", `/account/contact_users/${contactId}`);
  },

  createContact: async (payload) => {
    try {
      const response = await apiRequest("post", `/account/contact_users/`, payload);
      
      // Check if response indicates failure
      if (response?.data?.success === false) {
        const lang = getCurrentLanguage();
        const defaultMessage = lang === "ar" ? "خطأ في إنشاء جهة الاتصال" : "Error creating contact";
        const errorMessage = parseErrorDetails(response?.data?.details, defaultMessage);
        
        // Create an error object that matches axios error structure
        const error = new Error(errorMessage);
        error.response = {
          data: {
            error: errorMessage,
            ...response.data
          }
        };
        throw error;
      }
      
      return response;
    } catch (error) {
      // If error already has response data with details, parse it
      if (error.response?.data?.details && !error.response?.data?.error) {
        const lang = getCurrentLanguage();
        const defaultMessage = lang === "ar" ? "خطأ في إنشاء جهة الاتصال" : "Error creating contact";
        error.response.data.error = parseErrorDetails(error.response.data.details, defaultMessage);
      }
      throw error;
    }
  },

  deleteExcelFile: async (payload) => {
    try {
      const response = await apiRequest("post", `/account/contact_users/delete_excel/`, payload);
      
      // Check if response indicates failure
      if (response?.data?.success === false) {
        const lang = getCurrentLanguage();
        const defaultMessage = lang === "ar" ? "خطأ في حذف ملف Excel" : "Error deleting Excel file";
        const errorMessage = parseErrorDetails(response?.data?.details, defaultMessage);
        
        const error = new Error(errorMessage);
        error.response = {
          data: {
            error: errorMessage,
            ...response.data
          }
        };
        throw error;
      }
      
      return response;
    } catch (error) {
      if (error.response?.data?.details && !error.response?.data?.error) {
        const lang = getCurrentLanguage();
        const defaultMessage = lang === "ar" ? "خطأ في حذف ملف Excel" : "Error deleting Excel file";
        error.response.data.error = parseErrorDetails(error.response.data.details, defaultMessage);
      }
      throw error;
    }
  },

  uploadContactExcel: async (file, contactId) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (contactId) {
        formData.append('contact_id', contactId);
      }

      const response = await apiRequest("post", `/account/campaigns/upload_campaign_excel/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Check if response indicates failure
      if (response?.data?.success === false) {
        const lang = getCurrentLanguage();
        const defaultMessage = lang === "ar" ? "خطأ في رفع ملف Excel" : "Error uploading Excel file";
        const errorMessage = parseErrorDetails(response?.data?.details, defaultMessage);
        
        const error = new Error(errorMessage);
        error.response = {
          data: {
            error: errorMessage,
            ...response.data
          }
        };
        throw error;
      }
      
      return response;
    } catch (error) {
      // Handle nested error format: {"error": "{'error': '...', 'errorArabic': '...'}"}
      if (error.response?.data?.error) {
        const errorData = error.response.data.error;
        
        // Try to parse if it's a stringified object
        if (typeof errorData === 'string' && (errorData.includes("'error':") || errorData.includes('"error":'))) {
          try {
            // Replace single quotes with double quotes for valid JSON, but preserve quotes inside strings
            let jsonStr = errorData;
            
            // Handle Python dict format: {'error': '...', 'errorArabic': '...'}
            // First, replace the outer single quotes with double quotes
            jsonStr = jsonStr.replace(/'/g, '"');
            
            const parsedError = JSON.parse(jsonStr);
            
            const lang = getCurrentLanguage();
            const finalMessage = lang === "ar" 
              ? (parsedError.errorArabic || parsedError.error)
              : (parsedError.error || parsedError.errorArabic);
            
            // Update the error object with the parsed message
            if (!error.response) error.response = {};
            if (!error.response.data) error.response.data = {};
            error.response.data.error = finalMessage;
            error.message = finalMessage;
          } catch (parseError) {
            // If parsing fails, try regex extraction as fallback
            console.error("Failed to parse error, trying regex:", parseError);
            try {
              const lang = getCurrentLanguage();
              let extractedMessage;
              
              if (lang === "ar") {
                // Try to extract errorArabic first
                const arabicMatch = errorData.match(/'errorArabic':\s*"([^"]+)"|"errorArabic":\s*"([^"]+)"/);
                const errorMatch = errorData.match(/'error':\s*"([^"]+)"|"error":\s*"([^"]+)"/);
                extractedMessage = (arabicMatch && (arabicMatch[1] || arabicMatch[2])) || (errorMatch && (errorMatch[1] || errorMatch[2]));
              } else {
                // Try to extract error first
                const errorMatch = errorData.match(/'error':\s*"([^"]+)"|"error":\s*"([^"]+)"/);
                const arabicMatch = errorData.match(/'errorArabic':\s*"([^"]+)"|"errorArabic":\s*"([^"]+)"/);
                extractedMessage = (errorMatch && (errorMatch[1] || errorMatch[2])) || (arabicMatch && (arabicMatch[1] || arabicMatch[2]));
              }
              
              if (extractedMessage) {
                if (!error.response) error.response = {};
                if (!error.response.data) error.response.data = {};
                error.response.data.error = extractedMessage;
                error.message = extractedMessage;
              }
            } catch (regexError) {
              console.error("Regex extraction also failed:", regexError);
            }
          }
        }
      } else if (error.response?.data?.details) {
        const lang = getCurrentLanguage();
        const defaultMessage = lang === "ar" ? "خطأ في رفع ملف Excel" : "Error uploading Excel file";
        error.response.data.error = parseErrorDetails(error.response.data.details, defaultMessage);
      }
      throw error;
    }
  },
};
